import { Component, DestroyRef, inject, OnInit, effect, WritableSignal, Signal, ViewChild, AfterViewInit, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@ngneat/transloco';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { Feature } from '@api/models';
import { PeliasService, ReverseGeocodingService } from '@api/services';
import { UtilsService } from '@shared/services/utils.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MapService } from '@features/planification-map/components/map/services/map.service';
import { map, take, tap } from 'rxjs';
import { Coordinates } from '../../../../../../api/models/inverse-geocoding.model';
import { SelectRoutePlacesService } from './services/select-route-places.service';
import { CoreService } from '@core/services';
import {MatDividerModule} from '@angular/material/divider';
import { MatOptionSelectionChange } from '@angular/material/core';

@Component({
  selector: 'app-select-route-form',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, MatDividerModule, ReactiveFormsModule, MatIconModule, MatInputModule, MatButtonModule, MatIconModule, MatAutocompleteModule, OverlayModule],
  templateUrl: './select-route-form.component.html',
  styleUrls: ['./select-route-form.component.scss']
})
export class SelectRouteFormComponent implements OnInit, AfterViewInit {

  @ViewChild('fromAutocomplete') fromAutocompleteReference!: MatAutocomplete;
  @ViewChild('toAutocomplete') toAutocompleteReference!: MatAutocomplete;
  @ViewChild('fromInput', { read: MatAutocompleteTrigger }) fromTrigger!: MatAutocompleteTrigger;
  @ViewChild('toInput', { read: MatAutocompleteTrigger }) toTrigger!: MatAutocompleteTrigger;

  private destroyRef = inject(DestroyRef)
  public peliasService = inject(PeliasService);
  private utilsService = inject(UtilsService);
  private coreService = inject(CoreService);
  private selectRoutePlacesService = inject(SelectRoutePlacesService);
  public selectedFromOption = this.selectRoutePlacesService.selectedFromOption;
  public selectedToOption = this.selectRoutePlacesService.selectedToOption;
  private mapService = inject(MapService);
  private reverseGeocodingService = inject(ReverseGeocodingService);

  public loadingRouteResults = toSignal(this.coreService.loadingRouteResults) as Signal<boolean>;

  public loading = false;

  public fromOptions:Feature[] = [];

  public toOptions:Feature[] = [];

  public selectRouteForm = new FormGroup({
    from: new FormControl(''),
    to: new FormControl(''),
  });
  
  public constructor() {
    effect(() => {
      const fromValue = this.selectedFromOption();
      this.selectRouteForm.controls.from.setValue(fromValue?.properties.name ? fromValue.properties.name : '', {emitEvent: false});
      if (!fromValue) {
        this.fromOptions = [];
        if (this.fromAutocompleteReference) {
          this.utilsService.cleanAutocomplete(this.fromAutocompleteReference);
        }
      }
    }, {allowSignalWrites: true})
    effect(() => {
      const toValue = this.selectedToOption();
      this.selectRouteForm.controls.to.setValue(toValue?.properties.name ? toValue.properties.name : '', {emitEvent: false});
      if (!toValue) {
        this.toOptions = [];
        if (this.toAutocompleteReference) {
          this.utilsService.cleanAutocomplete(this.toAutocompleteReference);
        }
      }
    }, {allowSignalWrites: true})
    effect(() => {
      if(this.loadingRouteResults()) {
        this.selectRouteForm.disable();
      } else {
        this.selectRouteForm.enable();
      }
    })
  }

  ngOnInit(): void {
    this.listenFromChanges();
    this.listenToChanges();
  }

  ngAfterViewInit(): void {
    this.listenAutocompleteTriggersForBlur();
  }

  private listenAutocompleteTriggersForBlur() {
    this.fromTrigger.panelClosingActions.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((trigger) => {
      if(!trigger) {
        if(this.fromOptions.length > 0) {
          this.fromTrigger.autocomplete.options.first.select();
          this.selectedFromOption.set(this.fromOptions[0]);
        } else {
          this.cleanFromButtonClicked(this.fromAutocompleteReference);
        }
      }
    });
    this.toTrigger.panelClosingActions.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((trigger) => {
      if(!trigger) {
        if(this.toOptions.length > 0) {
          this.toTrigger.autocomplete.options.first.select();
          this.selectedToOption.set(this.toOptions[0]);
        } else {
          this.cleanToButtonClicked(this.toAutocompleteReference);
        }
      }
    });
  }
  

  public swapValues(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    
    const fromValue = this.selectRouteForm.controls.from.value;
    this.selectRouteForm.controls.from.setValue(this.selectRouteForm.controls.to.value);
    this.selectRouteForm.controls.to.setValue(fromValue);
    //change options array
    const fromOptions = [...this.fromOptions];
    this.fromOptions = this.toOptions;
    this.toOptions = fromOptions;

    const selectedFrom = this.selectedFromOption();
    this.selectedFromOption.set(this.selectedToOption());
    this.selectedToOption.set(selectedFrom);
  }

  private listenFromChanges() {
    this.utilsService.applyRxJSOperatorsToFeatureAutocomplete(this.selectRouteForm, 'from', this.selectedFromOption.asReadonly(), this.destroyRef)
    .pipe(takeUntilDestroyed(this.destroyRef),tap(() => this.loading = true))
    .subscribe((features) => {
      this.loading = false;
      this.fromOptions = features ?? [];
    });
  }

  private listenToChanges() {
    this.utilsService.applyRxJSOperatorsToFeatureAutocomplete(this.selectRouteForm, 'to', this.selectedToOption.asReadonly(), this.destroyRef)
    .pipe(takeUntilDestroyed(this.destroyRef),tap(() => this.loading = true))
    .subscribe((features) => {
      this.loading = false;
      this.toOptions = features ?? [];
    });
  }

  public onFromOptionSelected(event: MatOptionSelectionChange, option: Feature) {
    if (event.isUserInput) {
      this.selectedFromOption.set(option);
    }

  }
  
  public onToOptionSelected(event: MatOptionSelectionChange, option: Feature) {
    if (event.isUserInput) {
      this.selectedToOption.set(option);
    }
  }

  public fromIntroClicked() {
    if (this.fromOptions.length === 0) {
      this.cleanFromButtonClicked(this.fromAutocompleteReference);
    }
  }

  public toIntroClicked() {
    if (this.toOptions.length === 0) {
      this.cleanToButtonClicked(this.toAutocompleteReference);
    }
  }

  public myLocationClicked(fieldToUpdate:FormControl, signalToUpdate:WritableSignal<Feature|null>, auto?: MatAutocomplete) {
    let coordinates = this.mapService.getCurrentCoordinates();
    this.utilsService.cleanAutocomplete(auto);
    if(!coordinates){ return; }
    this.doReverseGeocodingRequest(coordinates, fieldToUpdate, signalToUpdate,true);
  }

  private cleanButtonClicked(fieldToUpdate:FormControl, signalToUpdate:WritableSignal<Feature|null>) {
    fieldToUpdate.reset('');
    signalToUpdate.set(null);
  }


  public cleanFromButtonClicked(auto: MatAutocomplete) {
    this.fromOptions = [];
    this.utilsService.cleanAutocomplete(auto);
    this.cleanButtonClicked(this.selectRouteForm.controls.from, this.selectedFromOption);
  }

  public cleanToButtonClicked(auto: MatAutocomplete) {
    this.toOptions = [];
    this.utilsService.cleanAutocomplete(auto);
    this.cleanButtonClicked(this.selectRouteForm.controls.to, this.selectedToOption);
  }

  public getCoordinatesFromClickMap(signalToUpdate:WritableSignal<Feature|null>, auto?: MatAutocomplete) {
    this.utilsService.cleanAutocomplete(auto);
    this.coreService.getCoordinatesFromClickMap(signalToUpdate).pipe(take(1)).subscribe()
  }

  private doReverseGeocodingRequest(coordinates: Coordinates, fieldToUpdate:FormControl, signalToUpdate:WritableSignal<Feature|null>, mylocation = false, waitingForClick = false) {
    this.reverseGeocodingService.reverseGeocoding(coordinates)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => fieldToUpdate.disable()),
      map((response) => response.features[0]),
    )
    .subscribe((response) => {
      fieldToUpdate.enable();
      if(!response) { 
        this.utilsService.showSnackBarError(mylocation? 'canNotGetYourUbication' : 'ubicationOutOfBounds');
        return; 
      }
      fieldToUpdate.setValue(response.properties.name, {emitEvent: false});
      signalToUpdate.set(response);
      if(waitingForClick) {
        this.coreService.sidenavOpened.set(true);
        this.mapService.waitingForClick.set(false);
      }
    });
  }


}
